import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  fetchProjects,
  fetchProjectById,
  fetchProjectByKey,
  createProject,
  updateProject,
  deleteProject,
  fetchProjectActivity,
  fetchProjectMembers,
  addProjectMember,
  removeProjectMember,
  fetchBoards,
  reorderStatuses,
  fetchLabels,
  fetchCustomFields,
  fetchIssues,
  fetchIssueById,
  fetchIssueByKey,
  createIssue,
  updateIssue,
  moveIssue,
  deleteIssue,
  fetchSprints,
  createSprint,
  startSprint,
  completeSprint,
  updateSprint,
  deleteSprint,
  reorderSprints,
  fetchComponents,
  fetchVersions,
  fetchResolutions,
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchSavedFilters,
  fetchDependencies,
  addDependency,
  removeDependency,
  fetchBaselines,
  createBaseline,
  fetchBaselineDetail,
  deleteBaseline,
  autoSchedule,
  getCriticalPath,
  fetchCapacity,
} from "./thunks";

import type {
  ActiveCompany,
  BacklogFilters,
  BoardFilters,
  Issue,
  ProjectsState,
} from "./types";

const DEFAULT_COMPANY: ActiveCompany = {
  company_id: "",
  company_name: "Select Company",
  company_type: "Type",
};

const DEFAULT_BOARD_FILTERS: BoardFilters = {
  epic_id: null,
  assignee_id: null,
  label_ids: [],
  q: "",
};

const DEFAULT_BACKLOG_FILTERS: BacklogFilters = {
  epic_id: null,
  assignee_id: null,
  label_ids: [],
  q: "",
};

const initialState: ProjectsState = {
  activeCompany: DEFAULT_COMPANY,

  projects: [],
  projectsLoading: false,
  currentProject: null,
  currentProjectLoading: false,

  currentBoard: null,
  boards: [],
  statuses: [],

  members: [],
  membersLoading: false,

  labels: [],
  customFields: [],

  components: [],
  versions: [],
  resolutions: [],

  issues: {},
  issuesByProject: {},
  issuesLoading: false,
  activeIssue: null,
  activeIssueLoading: false,

  sprints: [],
  sprintsLoading: false,

  activity: [],
  activityLoading: false,

  notifications: [],
  notificationsLoading: false,
  unreadNotificationCount: 0,

  savedFilters: [],

  dependencies: [],
  dependenciesLoading: false,
  baselines: [],
  baselinesLoading: false,
  activeBaseline: null,
  scheduleResult: null,
  criticalPath: [],
  schedulingLoading: false,

  capacity: [],
  capacityLoading: false,

  boardFilters: DEFAULT_BOARD_FILTERS,
  backlogFilters: DEFAULT_BACKLOG_FILTERS,

  error: null,
};

const upsertIssue = (state: ProjectsState, issue: Issue) => {
  state.issues[issue.id] = issue;
  const projectIds = state.issuesByProject[issue.project_id] || [];
  if (!projectIds.includes(issue.id)) {
    state.issuesByProject[issue.project_id] = [...projectIds, issue.id];
  }
};

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    setActiveCompany: (state, action: PayloadAction<ActiveCompany>) => {
      state.activeCompany = action.payload;
    },
    setBoardFilters: (state, action: PayloadAction<Partial<BoardFilters>>) => {
      state.boardFilters = { ...state.boardFilters, ...action.payload };
    },
    resetBoardFilters: (state) => {
      state.boardFilters = DEFAULT_BOARD_FILTERS;
    },
    setBacklogFilters: (
      state,
      action: PayloadAction<Partial<BacklogFilters>>
    ) => {
      state.backlogFilters = { ...state.backlogFilters, ...action.payload };
    },
    resetBacklogFilters: (state) => {
      state.backlogFilters = DEFAULT_BACKLOG_FILTERS;
    },
    setActiveIssue: (state, action: PayloadAction<Issue | null>) => {
      state.activeIssue = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.projectsLoading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projectsLoading = false;
        const payload = action.payload;
        state.projects = Array.isArray(payload?.projects)
          ? payload.projects
          : Array.isArray(payload)
          ? payload
          : [];
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.projectsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchProjectById.pending, (state) => {
        state.currentProjectLoading = true;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.currentProjectLoading = false;
        const projectData = action.payload?.project ?? action.payload ?? null;
        if (projectData && action.payload?.stats) {
          projectData.stats = action.payload.stats;
        }
        state.currentProject = projectData;
        if (action.payload?.boards) state.boards = action.payload.boards;
        if (action.payload?.statuses) state.statuses = action.payload.statuses;
        if (action.payload?.members) state.members = action.payload.members;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.currentProjectLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchProjectByKey.pending, (state) => {
        state.currentProjectLoading = true;
      })
      .addCase(fetchProjectByKey.fulfilled, (state, action) => {
        state.currentProjectLoading = false;
        const projectData = action.payload?.project ?? action.payload ?? null;
        if (projectData && action.payload?.stats) {
          projectData.stats = action.payload.stats;
        }
        state.currentProject = projectData;
        if (action.payload?.boards) state.boards = action.payload.boards;
        if (action.payload?.statuses) state.statuses = action.payload.statuses;
        if (action.payload?.members) state.members = action.payload.members;
      })
      .addCase(fetchProjectByKey.rejected, (state, action) => {
        state.currentProjectLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createProject.fulfilled, (state, action) => {
        const project = action.payload?.project ?? action.payload;
        if (project) {
          state.projects = [project, ...state.projects];
        }
      })

      .addCase(updateProject.fulfilled, (state, action) => {
        const project = action.payload?.project ?? action.payload;
        if (project) {
          state.projects = state.projects.map((p) =>
            p.id === project.id ? { ...p, ...project } : p
          );
          if (state.currentProject?.id === project.id) {
            state.currentProject = { ...state.currentProject, ...project };
          }
        }
      })

      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter((p) => p.id !== action.payload);
      })

      .addCase(fetchProjectActivity.pending, (state) => {
        state.activityLoading = true;
      })
      .addCase(fetchProjectActivity.fulfilled, (state, action) => {
        state.activityLoading = false;
        state.activity = Array.isArray(action.payload?.activity)
          ? action.payload.activity
          : Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchProjectActivity.rejected, (state, action) => {
        state.activityLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchProjectMembers.pending, (state) => {
        state.membersLoading = true;
      })
      .addCase(fetchProjectMembers.fulfilled, (state, action) => {
        state.membersLoading = false;
        state.members = Array.isArray(action.payload?.members)
          ? action.payload.members
          : Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchProjectMembers.rejected, (state, action) => {
        state.membersLoading = false;
        state.error = action.payload as string;
      })

      .addCase(addProjectMember.fulfilled, (state, action) => {
        const member = action.payload?.member ?? action.payload;
        if (member) state.members = [...state.members, member];
      })
      .addCase(removeProjectMember.fulfilled, (state, action) => {
        state.members = state.members.filter((m) => m.id !== action.payload);
      })

      .addCase(fetchBoards.fulfilled, (state, action) => {
        state.boards = Array.isArray(action.payload?.boards)
          ? action.payload.boards
          : Array.isArray(action.payload)
          ? action.payload
          : [];
        if (action.payload?.statuses) state.statuses = action.payload.statuses;
      })

      .addCase(reorderStatuses.fulfilled, (state, action) => {
        const statuses = action.payload?.statuses;
        if (Array.isArray(statuses)) state.statuses = statuses;
      })

      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.labels = Array.isArray(action.payload?.labels)
          ? action.payload.labels
          : Array.isArray(action.payload)
          ? action.payload
          : [];
      })

      .addCase(fetchCustomFields.fulfilled, (state, action) => {
        state.customFields = Array.isArray(action.payload?.custom_fields)
          ? action.payload.custom_fields
          : Array.isArray(action.payload)
          ? action.payload
          : [];
      })

      .addCase(fetchIssues.pending, (state) => {
        state.issuesLoading = true;
      })
      .addCase(fetchIssues.fulfilled, (state, action) => {
        state.issuesLoading = false;
        const issues: Issue[] = Array.isArray(action.payload?.issues)
          ? action.payload.issues
          : Array.isArray(action.payload)
          ? action.payload
          : [];
        issues.forEach((issue) => upsertIssue(state, issue));
      })
      .addCase(fetchIssues.rejected, (state, action) => {
        state.issuesLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchIssueById.pending, (state) => {
        state.activeIssueLoading = true;
      })
      .addCase(fetchIssueById.fulfilled, (state, action) => {
        state.activeIssueLoading = false;
        const issue = action.payload?.issue ?? action.payload;
        if (issue) {
          state.activeIssue = issue;
          upsertIssue(state, issue);
        }
      })
      .addCase(fetchIssueById.rejected, (state, action) => {
        state.activeIssueLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchIssueByKey.pending, (state) => {
        state.activeIssueLoading = true;
      })
      .addCase(fetchIssueByKey.fulfilled, (state, action) => {
        state.activeIssueLoading = false;
        const issue = action.payload?.issue ?? action.payload;
        if (issue) {
          state.activeIssue = issue;
          upsertIssue(state, issue);
        }
      })
      .addCase(fetchIssueByKey.rejected, (state, action) => {
        state.activeIssueLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createIssue.fulfilled, (state, action) => {
        const issue = action.payload?.issue ?? action.payload;
        if (issue) upsertIssue(state, issue);
      })
      .addCase(updateIssue.fulfilled, (state, action) => {
        const issue = action.payload?.issue ?? action.payload;
        if (issue) upsertIssue(state, issue);
      })
      .addCase(moveIssue.fulfilled, (state, action) => {
        const issue = action.payload?.issue ?? action.payload;
        if (issue) upsertIssue(state, issue);
      })
      .addCase(deleteIssue.fulfilled, (state, action) => {
        const id = action.payload;
        const issue = state.issues[id];
        if (issue) {
          delete state.issues[id];
          state.issuesByProject[issue.project_id] = (
            state.issuesByProject[issue.project_id] || []
          ).filter((iid) => iid !== id);
        }
      })

      .addCase(fetchSprints.pending, (state) => {
        state.sprintsLoading = true;
      })
      .addCase(fetchSprints.fulfilled, (state, action) => {
        state.sprintsLoading = false;
        state.sprints = Array.isArray(action.payload?.sprints)
          ? action.payload.sprints
          : Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchSprints.rejected, (state, action) => {
        state.sprintsLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createSprint.fulfilled, (state, action) => {
        const sprint = action.payload?.sprint ?? action.payload;
        if (sprint) state.sprints = [...state.sprints, sprint];
      })
      .addCase(startSprint.fulfilled, (state, action) => {
        const sprint = action.payload?.sprint ?? action.payload;
        if (sprint) {
          state.sprints = state.sprints.map((s) =>
            s.id === sprint.id ? { ...s, ...sprint } : s
          );
        }
      })
      .addCase(completeSprint.fulfilled, (state, action) => {
        const sprint = action.payload?.sprint ?? action.payload;
        if (sprint) {
          state.sprints = state.sprints.map((s) =>
            s.id === sprint.id ? { ...s, ...sprint } : s
          );
        }
      })

      .addCase(updateSprint.fulfilled, (state, action) => {
        const sprint = action.payload?.sprint ?? action.payload;
        if (sprint) {
          state.sprints = state.sprints.map((s) =>
            s.id === sprint.id ? { ...s, ...sprint } : s
          );
        }
      })

      .addCase(deleteSprint.fulfilled, (state, action) => {
        state.sprints = state.sprints.filter((s) => s.id !== action.payload);
      })

      .addCase(reorderSprints.fulfilled, (state, action) => {
        const updated = action.payload?.sprints;
        if (Array.isArray(updated)) {
          const map = new Map<number, any>(updated.map((s: any) => [s.id, s]));
          state.sprints = state.sprints.map((s) =>
            map.has(s.id) ? { ...s, ...map.get(s.id) } : s
          );
        }
      })

      .addCase(fetchComponents.fulfilled, (state, action) => {
        state.components = action.payload?.components || [];
      })

      .addCase(fetchVersions.fulfilled, (state, action) => {
        state.versions = action.payload?.versions || [];
      })

      .addCase(fetchResolutions.fulfilled, (state, action) => {
        state.resolutions = action.payload?.resolutions || [];
      })

      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notificationsLoading = false;
        state.notifications = action.payload?.notifications || [];
        state.unreadNotificationCount = action.payload?.unread_count ?? 0;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.notificationsLoading = false;
      })

      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const updated = action.payload?.notification;
        if (updated) {
          state.notifications = state.notifications.map((n) =>
            n.id === updated.id ? { ...n, read_at: updated.read_at } : n
          );
          state.unreadNotificationCount = Math.max(0, state.unreadNotificationCount - 1);
        }
      })

      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((n) => ({
          ...n,
          read_at: n.read_at || new Date().toISOString(),
        }));
        state.unreadNotificationCount = 0;
      })

      .addCase(fetchSavedFilters.fulfilled, (state, action) => {
        state.savedFilters = action.payload || [];
      })

      /* ─── R2: Dependencies ─── */
      .addCase(fetchDependencies.pending, (state) => {
        state.dependenciesLoading = true;
      })
      .addCase(fetchDependencies.fulfilled, (state, action) => {
        state.dependencies = action.payload || [];
        state.dependenciesLoading = false;
      })
      .addCase(fetchDependencies.rejected, (state) => {
        state.dependenciesLoading = false;
      })
      .addCase(addDependency.fulfilled, (state, action) => {
        if (action.payload) state.dependencies.push(action.payload);
      })
      .addCase(removeDependency.fulfilled, (state, action) => {
        state.dependencies = state.dependencies.filter((d) => d.id !== action.payload);
      })

      /* ─── R2: Baselines ─── */
      .addCase(fetchBaselines.pending, (state) => {
        state.baselinesLoading = true;
      })
      .addCase(fetchBaselines.fulfilled, (state, action) => {
        state.baselines = action.payload || [];
        state.baselinesLoading = false;
      })
      .addCase(fetchBaselines.rejected, (state) => {
        state.baselinesLoading = false;
      })
      .addCase(createBaseline.fulfilled, (state, action) => {
        if (action.payload) state.baselines.unshift(action.payload);
      })
      .addCase(fetchBaselineDetail.fulfilled, (state, action) => {
        state.activeBaseline = action.payload || null;
      })
      .addCase(deleteBaseline.fulfilled, (state, action) => {
        state.baselines = state.baselines.filter((b) => b.id !== action.payload);
        if (state.activeBaseline?.id === action.payload) state.activeBaseline = null;
      })

      /* ─── R2: Scheduling ─── */
      .addCase(autoSchedule.pending, (state) => {
        state.schedulingLoading = true;
      })
      .addCase(autoSchedule.fulfilled, (state, action) => {
        state.scheduleResult = action.payload || null;
        state.criticalPath = action.payload?.critical_path
          ? action.payload.schedule.filter((s: any) => s.is_critical)
          : [];
        state.schedulingLoading = false;
      })
      .addCase(autoSchedule.rejected, (state) => {
        state.schedulingLoading = false;
      })
      .addCase(getCriticalPath.fulfilled, (state, action) => {
        state.criticalPath = action.payload?.critical_path || [];
      })
      .addCase(fetchCapacity.pending, (state) => {
        state.capacityLoading = true;
      })
      .addCase(fetchCapacity.fulfilled, (state, action) => {
        state.capacityLoading = false;
        state.capacity = action.payload || [];
      })
      .addCase(fetchCapacity.rejected, (state) => {
        state.capacityLoading = false;
      });
  },
});

export const {
  setActiveCompany,
  setBoardFilters,
  resetBoardFilters,
  setBacklogFilters,
  resetBacklogFilters,
  setActiveIssue,
  clearError,
} = projectsSlice.actions;

export default projectsSlice.reducer;
